import React, { ReactNode } from "react";
import Head from "@/Molecules/Inertia/Head";
import Section from "@/Molecules/Section";
import LandingDistribution from "@/Organisms/Landing/LandingDistribution";
import LandingFacts from "@/Organisms/Landing/LandingFacts";
import LandingFaq from "@/Organisms/Landing/LandingFaq";
import LandingHero from "@/Organisms/Landing/LandingHero";
import LandingHighlights from "@/Organisms/Landing/LandingHighlights";
import LandingRequestForm from "@/Organisms/Landing/LandingRequestForm";
import { LandingProps } from "@/types/Landing";

export type LandingTemplateProps =
  Pick<LandingProps, 'slug' | 'title' | 'canonical' | 'structuredData' | 'paper' | 'place' | 'schedule' | 'format' | 'priceFrom' | 'otherPapers' | 'faq' | 'userPersonRelationships'>
  & {
  /**
   * shown above the hero
   */
  breadcrumbs?: ReactNode
  hero: {
    intro: ReactNode
    highlights: ReactNode[]
    backgroundImage?: string
    background?: string
  }
  /**
   * papers checked in the request form, kept by the page
   */
  selectedPapers: number[]
  onTogglePaper: (paperId: number) => void
  /**
   * second way to continue, below the request form: make the advertisement yourself
   */
  selfService: {
    title: string
    content: ReactNode
  }
  /**
   * sections between the self service section and the faq
   */
  children?: ReactNode
  /**
   * sections below the faq
   */
  footer?: ReactNode
}

/**
 * Layout of the "rouwadvertentie + krant/plaats" landing pages, the content and logic are defined by the page per domain
 */
export default ({
  slug,
  title,
  canonical,
  structuredData,
  paper,
  place,
  schedule,
  format,
  priceFrom,
  otherPapers,
  faq,
  userPersonRelationships,
  breadcrumbs,
  hero,
  selectedPapers,
  onTogglePaper,
  selfService,
  children,
  footer,
}: LandingTemplateProps) => {
  return <>
    <Head>
      <link rel="canonical" href={canonical} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(structuredData)}} />
    </Head>

    {breadcrumbs}

    <LandingHero
      title={title}
      paper={paper}
      hideLogo={!!place}
      backgroundImage={hero.backgroundImage}
      background={hero.background}
      intro={hero.intro}
    >
      <LandingHighlights items={hero.highlights} />
    </LandingHero>

    <Section lighter>
      <LandingRequestForm
        slug={slug}
        paper={paper}
        place={place}
        otherPapers={otherPapers}
        userPersonRelationships={userPersonRelationships}
        selected={selectedPapers}
        onToggle={onTogglePaper}
      />
    </Section>

    {/* Entered by place: nothing about one paper, the details per paper are in the request form */}
    {!place && <LandingFacts paper={paper} priceFrom={priceFrom} schedule={schedule} format={format} />}

    {!place && <LandingDistribution paper={paper} />}

    <Section
      white
      title={selfService.title}
      content={<>
        <a id={'zelf-maken'} />
        {selfService.content}
      </>}
    />

    {children}

    <LandingFaq faq={faq} />

    {footer}
  </>
}
